<script setup lang="ts">
import type { FaqForm } from '#build/components'

const { path } = useRoute()
const { getMoreFAQ } = useProductsStore()
const { 
  product,
  mainId
} = assertInject(ProductInjection)

const questions = computed<ProductQuestions>(() => product.value.faq.elements)
const totalQuestions = computed<number>(() => product.value.faq.total)
const hasQuestions = computed<boolean>(() => !!questions.value.length)
const hasMore = computed<boolean>(() => questions.value.length < totalQuestions.value)
const isFetching = computed<boolean>(() => product.value.faq.isFetching)

const form = ref<InstanceType<typeof FaqForm> | null>(null)
const submitting = ref(false)
const faqDialogOpen = ref<boolean>(false)
const focusAfterClose = ref<HTMLElement | null>(null)
</script>

<template>
  <div>
    <div v-if="!hasQuestions">
      <div class="pb-[15px]">
        Bądź pierwszym, który zada pytanie.
      </div>
      <div class="flex items-center">
        <button ref="focusAfterClose" class="btn btn-secondary basis-full md:basis-[calc(50%-5px)]" @click="faqDialogOpen = true">
          Dodaj pytanie
        </button>
      </div>
    </div>
    <div v-else>
      <FaqQuestion
        v-for="question in questions"
        :key="question.id"
        :author="question.title"
        :body="question.text"
        :published-date="question.createdAt"
        :answers="question.answers"
      />
      <div class="flex items-center justify-center flex-col gap-[10px] md:flex-row">
        <button
          class="btn btn-outline-secondary group disabled:pointer-events-none"
          :disabled="isFetching || !hasMore"
          @click="getMoreFAQ(mainId, path)"
        >
          <Transition enter-active-class="animate-zoom-in" leave-active-class="animate-zoom-out">
            <Spinner v-if="isFetching" class="group-hover:group-enabled:text-white text-secondary w-[20px] h-[20px]" />
            <span v-else class="inline-flex flex-row flex-nowrap items-center justify-center gap-[5px]">
              Pokaż więcej <i class="icon-arrow-button text-10 rotate-90" />
            </span>
          </Transition>
        </button>
        <button ref="focusAfterClose" class="btn btn-secondary" @click="faqDialogOpen = true">
          Dodaj pytanie
        </button>
      </div>
    </div>
    <ClientOnly>
      <Teleport to="body">
        <Transition.Dialog>
          <Dialog
            v-if="faqDialogOpen"
            esc-close
            :focus-after-closed="focusAfterClose"
            :initial-focus="form?.initialFocus"
            :close-disabled="submitting"
            aria-labelledby="dialog_faq_label"
            class="md:flex"
            @close="faqDialogOpen = false"
          >
            <Dialog.Overlay :closeable="form?.submitted" class="backdrop-blur bg-secondary/80" />
            <Dialog.Panel class="relative bg-white shadow-[rgb(0_0_0/20%)_0_3px_6px] overflow-hidden flex flex-col m-auto max-h-full w-full md:w-auto md:h-auto">
              <div class="flex items-center border-b border-gray-400 py-[10px] mx-[20px]">
                <h3 id="dialog_faq_label" class="font-medium text-20 text-action flex-1">
                  Pytanie o produkt
                </h3>
                <Dialog.Close
                  type="button"
                  aria-label="Zamknij"
                  class="text-20 transition-colors duration-500 ease-in-out hover:text-red focus:text-red"
                >
                  <i class="icon-close" />
                </Dialog.Close>
              </div>
              <div class="w-full md:w-[600px] mb-[20px] p-[20px_20px_0_20px] overflow-auto">
                <div class="font-medium text-16 mb-[20px]">
                  {{ product.name }}
                </div>
                <FaqForm ref="form" @submitting="$state => submitting = $state" />
              </div>
            </Dialog.Panel>
          </Dialog>
        </Transition.Dialog>
      </Teleport>
    </ClientOnly>
  </div>
</template>
